import React from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import { DownloadSimple, TrendUp } from "@phosphor-icons/react";
import api from "@/lib/api";
import { eur, eurCompact } from "@/lib/format";
import { PageHeader, StatCard } from "@/components/common";
import { Button } from "@/components/ui/button";
import { useTranslation } from 'react-i18next'

const NAVY = "#0B2545";
const GOLD = "#C5A059";
const PIE = [NAVY, GOLD, "#3b82c4", "#2f9e6f", "#c9772f", "#8a6d2f", "#64748b"];

function toCSV(rows) {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const body = rows.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(","));
  return [headers.join(","), ...body].join("\n");
}

export default function Reports() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({ queryKey: ["reports"], queryFn: async () => (await api.get("/reports")).data });

  const exportCSV = async (kind) => {
    const rows = (await api.get(`/${kind}`)).data;
    const clean = rows.map((r) => { const { media, ...rest } = r; return rest; });
    const csv = toCSV(clean);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${kind}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${kind}.csv`);
  };

  if (isLoading || !data)
    return <div className="flex justify-center py-40"><div className="animate-spin h-8 w-8 border-2 border-[#0B2545] border-t-transparent rounded-full" /></div>;

  const Chart = ({ title, data: d, testid }) => (
    <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid={testid}>
      <h3 className="font-serif text-lg text-slate-800 mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={d}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
          <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} />
          <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
          <Tooltip contentStyle={{ borderRadius: 4, border: t('1pxSolidE2e8f0', '1px solid #e2e8f0') }} />
          <Bar dataKey="value" fill={NAVY} radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  return (
    <div>
      <PageHeader
        title={t('reportsAnalytics', 'Reports & Analytics')}
        subtitle="Portfolio insights and exports"
        actions={
          <div className="flex flex-wrap gap-2">
            {["properties", "contacts", "opportunities", "tasks"].map((k) => (
              <Button key={k} variant="outline" onClick={() => exportCSV(k)} data-testid={`export-${k}`} className="rounded-sm capitalize">
                <DownloadSimple size={16} className="mr-1.5" /> {k}
              </Button>
            ))}
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <StatCard label={t('portfolioValue', 'Portfolio Value')} value={eurCompact(data.portfolio_value)} accent testid="report-portfolio" />
        <StatCard label={t('potentialCommission', 'Potential Commission')} value={eurCompact(data.potential_commission)} accent testid="report-commission" />
        <StatCard label={t('activeOpportunities', 'Active Opportunities')} value={data.active_opportunities} testid="report-opps" />
        <StatCard label={t('conversionRate', 'Conversion Rate')} value={`${data.conversion_rate}%`} icon={TrendUp} testid="report-conversion" sub={t('completedWonLostLost', '{{completed}} won · {{lost}} lost', { completed: data.completed, lost: data.lost })} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Chart title={t('propertiesByLocation', 'Properties by Location')} data={data.by_location} testid="chart-location" />
        <Chart title={t('propertiesByPriceBand', 'Properties by Price Band')} data={data.by_price} testid="chart-price" />
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h3 className="font-serif text-lg text-slate-800 mb-4">{t('byCategory', 'By Category')}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={data.by_category} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50}>
                {data.by_category.map((e, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 4, border: t('1pxSolidE2e8f0', '1px solid #e2e8f0') }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <Chart title={t('propertiesByStatus', 'Properties by Status')} data={data.by_status} testid="chart-status" />
      </div>
    </div>
  );
}
